import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { about, mission, vision } from "../mock";
import { Reveal } from "../hooks/useReveal";

const TABS = [
  { id: "about", label: "About" },
  { id: "mission", label: "Our Mission" },
  { id: "vision", label: "Our Vision" },
];

function AboutTab() {
  return (
    <div className="space-y-6 max-w-4xl">
      <p className="text-xl md:text-2xl font-display text-white leading-relaxed">
        {about.intro}
      </p>
      {about.paragraphs.map((p, i) => (
        <p key={i} className="text-[var(--af-muted)] leading-relaxed text-lg">
          {p}
        </p>
      ))}

      <div className="grid md:grid-cols-2 gap-8 pt-8">
        {[about.philosophy, about.commitment].map((block) => (
          <div
            key={block.title}
            className="p-8 rounded-2xl bg-[#121317] border border-[#1e2026]"
          >
            <h3 className="font-display font-semibold text-3xl text-white mb-5">
              {block.title}
            </h3>
            <div className="space-y-4">
              {block.paragraphs.map((p, i) => (
                <p key={i} className="text-[var(--af-muted)] leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="pt-6">
        <p className="font-display text-2xl md:text-3xl text-[var(--af-blue)] leading-snug border-l-2 border-[var(--af-blue)] pl-6">
          {about.signature}
        </p>
      </div>
    </div>
  );
}

function TextBlock({ data }) {
  return (
    <div className="max-w-4xl">
      <h2 className="font-display font-bold text-4xl md:text-5xl text-white mb-8">
        {data.title}
      </h2>
      <div className="space-y-6">
        {data.paragraphs.map((p, i) => (
          <p
            key={i}
            className={`leading-relaxed ${
              i === 0
                ? "text-xl md:text-2xl font-display text-white"
                : "text-lg text-[var(--af-muted)]"
            }`}
          >
            {p}
          </p>
        ))}
      </div>
    </div>
  );
}

export default function About() {
  const [tab, setTab] = useState("about");

  return (
    <>
      {/* Header */}
      <section className="relative pt-44 pb-20 bg-[#0a0b0d] border-b border-[#1c1e23] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 20%, var(--af-blue), transparent 45%)",
          }}
        />
        <div className="af-container relative z-10">
          <p className="eyebrow mb-5">Who We Are</p>
          <h1 className="font-display font-bold text-white text-6xl md:text-8xl leading-none">
            About August
          </h1>
          <p className="mt-6 text-lg text-[var(--af-muted)] max-w-2xl">
            Shaping spaces through thoughtful design, precision, and performance.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#0c0d0f]">
        <div className="af-container">
          {/* Tabs */}
          <div className="flex flex-wrap gap-3 mb-14 border-b border-[#1c1e23]">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`relative px-6 py-4 text-base md:text-lg font-medium transition-colors ${
                  tab === t.id
                    ? "text-white"
                    : "text-[var(--af-muted)] hover:text-white/80"
                }`}
              >
                {t.label}
                {tab === t.id && (
                  <span className="absolute left-0 bottom-[-1px] h-[2px] w-full bg-[var(--af-blue)]" />
                )}
              </button>
            ))}
          </div>

          <Reveal key={tab}>
            {tab === "about" && <AboutTab />}
            {tab === "mission" && <TextBlock data={mission} />}
            {tab === "vision" && <TextBlock data={vision} />}
          </Reveal>

          <div className="mt-16 pt-10 border-t border-[#1c1e23]">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0c0d0f] font-semibold rounded-full hover:bg-[var(--af-blue)] hover:text-white transition-all"
            >
              Start a Project With Us
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
